import React, { useState } from 'react';
import { PlusIcon, TrashIcon, SearchIcon, InfoIcon, FilterIcon } from 'lucide-react';
// Mock parts data
const mockPartsData = [{
  id: 'P001',
  reference: 'AIS-101-A',
  name: 'Control Panel',
  category: 'Electronics',
  unitPrice: 1250
}, {
  id: 'P002',
  reference: 'AIS-202-B',
  name: 'Gear Drive',
  category: 'Mechanical',
  unitPrice: 850
}, {
  id: 'P003',
  reference: 'AIS-303-C',
  name: 'Sprinkler Head',
  category: 'Irrigation',
  unitPrice: 45
}, {
  id: 'P004',
  reference: 'AIS-404-D',
  name: 'Drive Motor',
  category: 'Mechanical',
  unitPrice: 1100
}, {
  id: 'P005',
  reference: 'AIS-505-E',
  name: 'Pressure Gauge',
  category: 'Sensors',
  unitPrice: 75
}, {
  id: 'P006',
  reference: 'AIS-606-F',
  name: 'Solenoid Valve',
  category: 'Irrigation',
  unitPrice: 120
}, {
  id: 'P007',
  reference: 'AIS-707-G',
  name: 'GPS Module',
  category: 'Electronics',
  unitPrice: 550
}, {
  id: 'P008',
  reference: 'AIS-808-H',
  name: 'Tower Box',
  category: 'Structural',
  unitPrice: 350
}, {
  id: 'P009',
  reference: 'AIS-909-I',
  name: 'Span Pipe',
  category: 'Structural',
  unitPrice: 420
}, {
  id: 'P010',
  reference: 'AIS-010-J',
  name: 'End Gun',
  category: 'Irrigation',
  unitPrice: 980
}];
export function PartsSelectionStep({
  initialData = [],
  onUpdate
}) {
  const [selectedParts, setSelectedParts] = useState(initialData);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showPartsSelector, setShowPartsSelector] = useState(false);
  // Filter parts based on search and category
  const filteredParts = mockPartsData.filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchQuery.toLowerCase()) || part.reference.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || part.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  // Get unique categories for filter dropdown
  const categories = ['all', ...new Set(mockPartsData.map(part => part.category))];
  const handleAddPart = part => {
    // Check if part is already selected
    const existingPartIndex = selectedParts.findIndex(p => p.id === part.id);
    if (existingPartIndex >= 0) {
      // If already selected, increase quantity
      const newParts = [...selectedParts];
      newParts[existingPartIndex].quantity += 1;
      setSelectedParts(newParts);
      onUpdate(newParts);
    } else {
      // Otherwise add as new with quantity 1
      const newParts = [...selectedParts, {
        ...part,
        quantity: 1
      }];
      setSelectedParts(newParts);
      onUpdate(newParts);
    }
  };
  const handleRemovePart = index => {
    const newParts = selectedParts.filter((_, i) => i !== index);
    setSelectedParts(newParts);
    onUpdate(newParts);
  };
  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newParts = [...selectedParts];
    newParts[index].quantity = newQuantity;
    setSelectedParts(newParts);
    onUpdate(newParts);
  };
  // Calculate total
  const total = selectedParts.reduce((sum, part) => sum + part.unitPrice * part.quantity, 0);
  return <div className="space-y-6" data-id="element-5173">
      <div className="text-white text-xl font-medium mb-6" data-id="element-5174">
        Select Spare Parts
      </div>
      <div className="bg-blue-900/30 rounded-xl p-5 border border-blue-800/30 mb-6" data-id="element-5175">
        <div className="flex items-start" data-id="element-5176">
          <InfoIcon size={20} className="text-blue-400 mr-3 mt-0.5" data-id="element-5177" />
          <div data-id="element-5178">
            <h3 className="text-white font-medium mb-1" data-id="element-5179">
              Spare Parts Selection Guide
            </h3>
            <p className="text-blue-200 text-sm" data-id="element-5180">
              Select the spare parts you need for your irrigation system. You
              can search by name or reference number and filter by category. Add
              multiple items and adjust quantities as needed.
            </p>
          </div>
        </div>
      </div>

      {/* Selected Parts List */}
      <div className="bg-slate-800/50 rounded-xl border border-blue-800/30" data-id="element-5181">
        <div className="flex justify-between items-center p-4 border-b border-blue-800/30" data-id="element-5182">
          <h3 className="text-white font-medium" data-id="element-5183">Selected Parts</h3>
          <button onClick={() => setShowPartsSelector(true)} className="flex items-center px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors" data-id="element-5184">
            <PlusIcon size={16} className="mr-1" data-id="element-5185" />
            Add Parts
          </button>
        </div>
        {selectedParts.length > 0 ? <div data-id="element-5186">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-blue-800/30 text-sm font-medium text-blue-300" data-id="element-5187">
              <div className="col-span-1" data-id="element-5188">Ref</div>
              <div className="col-span-5" data-id="element-5189">Part Name</div>
              <div className="col-span-2" data-id="element-5190">Category</div>
              <div className="col-span-1 text-center" data-id="element-5191">Qty</div>
              <div className="col-span-2 text-right pr-8" data-id="element-5192">Price</div>
              <div className="col-span-1" data-id="element-5193"></div>
            </div>
            <div className="p-2" data-id="element-5194">
              {selectedParts.map((part, index) => <div key={index} className="grid grid-cols-12 gap-4 p-2 items-center" data-id="element-5195">
                  <div className="col-span-1 text-blue-300 text-sm" data-id="element-5196">
                    {part.reference}
                  </div>
                  <div className="col-span-5 text-white" data-id="element-5197">{part.name}</div>
                  <div className="col-span-2 text-blue-300 text-sm" data-id="element-5198">
                    {part.category}
                  </div>
                  <div className="col-span-1" data-id="element-5199">
                    <div className="flex items-center justify-center" data-id="element-5200">
                      <button onClick={() => handleQuantityChange(index, part.quantity - 1)} className="w-6 h-6 bg-slate-900/50 text-blue-300 rounded-l-md hover:bg-slate-900 hover:text-white" data-id="element-5201">
                        -
                      </button>
                      <input type="number" value={part.quantity} onChange={e => handleQuantityChange(index, parseInt(e.target.value) || 1)} className="w-10 h-6 bg-slate-900/50 border-y border-blue-800/30 text-white text-center focus:outline-none" min="1" data-id="element-5202" />
                      <button onClick={() => handleQuantityChange(index, part.quantity + 1)} className="w-6 h-6 bg-slate-900/50 text-blue-300 rounded-r-md hover:bg-slate-900 hover:text-white" data-id="element-5203">
                        +
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2 text-right text-white pr-8" data-id="element-5204">
                    ${(part.unitPrice * part.quantity).toFixed(2)}
                  </div>
                  <div className="col-span-1 flex justify-center" data-id="element-5205">
                    <button onClick={() => handleRemovePart(index)} className="p-1.5 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-white transition-colors" data-id="element-5206">
                      <TrashIcon size={16} data-id="element-5207" />
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="p-4 border-t border-blue-800/30 flex justify-end" data-id="element-5208">
              <div className="w-64" data-id="element-5209">
                <div className="flex justify-between items-center text-blue-300 mb-2" data-id="element-5210">
                  <span data-id="element-5211">Subtotal:</span>
                  <span className="text-white" data-id="element-5212">${total.toFixed(2)}</span>
                </div>
                <div className="border-t border-blue-800/30 pt-2 mt-2" data-id="element-5213">
                  <div className="flex justify-between items-center" data-id="element-5214">
                    <span className="font-medium text-white" data-id="element-5215">Total:</span>
                    <span className="text-xl font-bold text-white" data-id="element-5216">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="p-8 text-center" data-id="element-5217">
            <p className="text-blue-300 mb-3" data-id="element-5218">No parts selected yet</p>
            <button onClick={() => setShowPartsSelector(true)} className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors" data-id="element-5219">
              <PlusIcon size={18} className="mr-2" data-id="element-5220" />
              Select Parts
            </button>
          </div>}
      </div>

      {/* Parts Selector Modal */}
      {showPartsSelector && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-5221">
          <div className="bg-slate-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-5222">
            <div className="p-4 border-b border-blue-800/30 flex justify-between items-center" data-id="element-5223">
              <h3 className="text-lg font-medium text-white" data-id="element-5224">Select Parts</h3>
              <button onClick={() => setShowPartsSelector(false)} className="text-blue-300 hover:text-white" data-id="element-5225">
                <span className="text-2xl" data-id="element-5226">&times;</span>
              </button>
            </div>
            <div className="p-4 border-b border-blue-800/30 bg-slate-800/50 flex flex-wrap gap-4" data-id="element-5227">
              <div className="relative flex-1 min-w-[250px]" data-id="element-5228">
                <SearchIcon size={18} className="absolute left-3 top-2.5 text-blue-400" data-id="element-5229" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900/70 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search by name or reference..." data-id="element-5230" />
              </div>
              <div className="relative min-w-[200px]" data-id="element-5231">
                <FilterIcon size={18} className="absolute left-3 top-2.5 text-blue-400" data-id="element-5232" />
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="w-full bg-slate-900/70 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238BABBF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              backgroundSize: '20px'
            }} data-id="element-5233">
                  {categories.map(category => <option key={category} value={category} data-id="element-5234">
                      {category === 'all' ? 'All Categories' : category}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="overflow-auto flex-1" data-id="element-5235">
              <table className="w-full border-collapse" data-id="element-5236">
                <thead className="bg-slate-800 sticky top-0" data-id="element-5237">
                  <tr data-id="element-5238">
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5239">
                      Reference
                    </th>
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5240">
                      Name
                    </th>
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5241">
                      Category
                    </th>
                    <th className="text-right p-3 text-blue-300 font-medium" data-id="element-5242">
                      Price
                    </th>
                    <th className="p-3" data-id="element-5243"></th>
                  </tr>
                </thead>
                <tbody data-id="element-5244">
                  {filteredParts.map(part => <tr key={part.id} className="border-b border-blue-900/30 hover:bg-slate-800/50" data-id="element-5245">
                      <td className="p-3 text-blue-300" data-id="element-5246">{part.reference}</td>
                      <td className="p-3 text-white" data-id="element-5247">{part.name}</td>
                      <td className="p-3 text-blue-300" data-id="element-5248">{part.category}</td>
                      <td className="p-3 text-white text-right" data-id="element-5249">
                        ${part.unitPrice.toFixed(2)}
                      </td>
                      <td className="p-3 text-right" data-id="element-5250">
                        <button onClick={() => handleAddPart(part)} className="px-3 py-1 bg-blue-600/30 text-blue-300 rounded hover:bg-blue-600 hover:text-white transition-colors" data-id="element-5251">
                          Add
                        </button>
                      </td>
                    </tr>)}
                  {filteredParts.length === 0 && <tr data-id="element-5252">
                      <td colSpan={5} className="p-6 text-center text-blue-300" data-id="element-5253">
                        No parts found matching your search criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-blue-800/30 bg-slate-800/50 flex justify-end" data-id="element-5254">
              <button onClick={() => setShowPartsSelector(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-5255">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}